import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/causes", label: "Causes" },
  { to: "/give", label: "Give" },
  { to: "/about", label: "About" },
  { to: "/sponsor", label: "Sponsor an Ambassador" },
  { to: "/ambassador", label: "Become an Ambassador" },
  { to: "/contact", label: "Contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      className="sticky top-0 z-50 border-b border-line bg-ivory/90 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <Link to="/" aria-label="MyHeartGives home" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-crimson" : "text-cocoa hover:text-ink"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <span
            className="rounded-full border border-gold/50 bg-gold-soft px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-crimson"
            data-testid="nav-launch-badge"
          >
            Opening Soon
          </span>
          <Link
            to="/give"
            className="rounded-full bg-crimson px-5 py-2.5 text-sm font-semibold text-ivory transition-colors duration-200 hover:bg-crimson-deep"
            data-testid="nav-cta-button"
          >
            Join the Launch
          </Link>
        </div>

        <button
          className="rounded-lg p-2 text-ink lg:hidden"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ivory px-6 py-5"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                className="rounded-lg p-2 text-ink"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block border-b border-line py-4 font-serif text-3xl font-semibold ${
                      location.pathname === l.to ? "text-crimson" : "text-ink"
                    }`}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="mt-auto font-mono text-xs uppercase tracking-[0.25em] text-taupe">
              Opening Soon
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-ink text-ivory" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <div>
          <Logo dark />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-ivory/70">
            A cause-marketing community, built with heart in the hills of West Virginia.
          </p>
        </div>
        <nav className="flex flex-col gap-2.5" data-testid="footer-nav">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm text-ivory/80 transition-colors hover:text-gold-bright"
              data-testid={`footer-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col gap-2.5 text-sm text-ivory/80">
          <span className="flex items-center gap-2" data-testid="footer-phone-label">
            <Phone className="h-4 w-4 text-gold" /> Phone
          </span>
          <a
            href="mailto:info@myheartgives.com"
            className="transition-colors hover:text-gold-bright"
            data-testid="footer-email-link"
          >
            Email — info@myheartgives.com
          </a>
          <span data-testid="footer-domain">MyHeartGives.com</span>
          <Link
            to="/admin"
            className="mt-2 text-xs text-ivory/40 transition-colors hover:text-ivory/70"
            data-testid="footer-admin-link"
          >
            Admin
          </Link>
        </div>
      </div>
      <div className="mt-12 flex flex-col gap-2 border-t border-ivory/15 pt-6 text-xs text-ivory/60 sm:flex-row sm:items-center sm:justify-between">
        <span>© 2026 MyHeartGives. All hearts reserved.</span>
        <span className="font-serif italic text-gold-bright" data-testid="footer-tagline">
          Powered by people who care
        </span>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-ivory">
    <Nav />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
